import React, { useState, useEffect } from 'react';
import { Box, Button, Select, MenuItem, Typography, FormControl, InputLabel, CircularProgress, Chip, Dialog } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import ThemedSelect from '../../utils/ThemedSelect';
import { useOrganization } from '../../utils/OrganizationContext';

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = '#0F1B2A';
const INK_2 = '#3D4C60';
const MUTED = '#66748A';
const LINE = '#E4E9EF';
const FIELD_EDGE = '#D5DEE7';
const PRIMARY = '#0B5C8A';
const ERROR = '#C42B24';
const FB_BLUE = '#1877F2';

const cardSx = {
    bgcolor: '#FFFFFF',
    border: `1px solid ${LINE}`,
    borderRadius: '14px',
    overflow: 'hidden',
    mb: 2,
};
const cardHeadSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    px: 2.5,
    py: 1.5,
    borderBottom: '1px solid #EDF1F5',
};
const cardTitle = {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    fontFamily: FONT,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.11em',
    textTransform: 'uppercase',
    color: INK_2,
};
const dot = (c) => ({ width: 6, height: 6, borderRadius: '50%', background: c, flex: '0 0 6px' });
const lbl = { display: 'block', marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const textInp = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    height: 44,
    padding: '0 12px',
    margin: 0,
    background: '#FFFFFF',
    border: `1px solid ${FIELD_EDGE}`,
    borderRadius: 10,
    fontFamily: FONT,
    fontSize: 14,
    color: INK,
    outline: 'none',
};
const primaryBtn = {
    height: 42,
    px: 2.5,
    borderRadius: '10px',
    bgcolor: PRIMARY,
    color: '#FFFFFF',
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': { bgcolor: '#084767', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: PRIMARY, color: '#FFFFFF', opacity: 0.5 },
};
const ghostBtn = {
    height: 32,
    px: 1.5,
    borderRadius: '8px',
    border: `1px solid ${FIELD_EDGE}`,
    background: '#FFFFFF',
    color: INK,
    fontFamily: FONT,
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { background: '#F4F7FA' },
};
const dangerBtn = {
    ...ghostBtn,
    border: '1px solid #F2CFCA',
    color: '#A6342A',
    '&:hover': { background: '#FBEDEB', borderColor: '#F2CFCA' },
};

const FacebookIntegration = () => {
    const [isConnected, setIsConnected] = useState(false);
    const [pages, setPages] = useState([]);
    const [forms, setForms] = useState([]);
    const [selectedPage, setSelectedPage] = useState('');
    const [selectedForm, setSelectedForm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Project routing + saved connections (page + form -> project)
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [connectionName, setConnectionName] = useState('');
    const [mappings, setMappings] = useState([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editProject, setEditProject] = useState('');
    const [confirm, setConfirm] = useState(null); // { title, msg, action, label }
    const { role } = useOrganization();

    const location = useLocation();
    const navigate = useNavigate();

    const storedUser = JSON.parse(localStorage.getItem('user'));
    const orgRaw = localStorage.getItem('org');
    const orgId = orgRaw ? orgRaw.replace(/"/g, '') : '';

    const headers = {
        Authorization: `Bearer ${storedUser?.accessToken}`,
        orgId: orgId
    };

    const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/facebook`;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');

        if (code) {
            handleCallback(code);
        } else {
            checkConnection();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Projects (same endpoints as Add Lead / Edit Lead)
    useEffect(() => {
        if (!orgId) return;
        const fetchProjects = async () => {
            try {
                const url = role === 'ADMIN'
                    ? `${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`
                    : `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`;
                const res = await axios.get(url, { headers: { Authorization: `Bearer ${storedUser?.accessToken}` } });
                setProjects(res.data || []);
            } catch (error) {
                console.log(error);
            }
        };
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, role]);

    const fetchMappings = async () => {
        try {
            const res = await axios.get(`${API_BASE}/settings`, { headers });
            setMappings(res.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    const handleCallback = async (code) => {
        try {
            navigate('/dashboard/integrations/facebook', { replace: true });
            await axios.get(`${API_BASE}/callback?code=${code}`, { headers });
            toast.success('Successfully authenticated with Facebook!');
            checkConnection();
        } catch (error) {
            toast.error('Failed to complete Facebook authentication.');
            setIsLoading(false);
        }
    };

    const checkConnection = async () => {
        try {
            const res = await axios.get(`${API_BASE}/pages`, { headers });
            if (res.status === 200) {
                setIsConnected(true);
                setPages(res.data || []);
                fetchMappings();
            }
        } catch (error) {
            setIsConnected(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConnect = async () => {
        try {
            const res = await axios.get(`${API_BASE}/connect`, { headers });
            window.location.href = res.data;
        } catch (error) {
            toast.error("Failed to initiate Facebook connection.");
        }
    };

    const doDisconnect = async () => {
        try {
            await axios.delete(`${API_BASE}/disconnect`, { headers });
            setIsConnected(false);
            setPages([]);
            setForms([]);
            setSelectedPage('');
            setSelectedForm('');
            setSelectedProject('');
            setMappings([]);
            toast.success("Facebook disconnected successfully.");
        } catch (error) {
            toast.error("Failed to disconnect Facebook.");
        }
    };

    const handleDisconnect = () =>
        setConfirm({
            title: 'Disconnect Facebook?',
            msg: `All ${mappings.length || ''} connected forms will be removed and new Facebook leads will stop coming in.`,
            label: 'Disconnect',
            action: doDisconnect,
        });

    const handlePageChange = async (e) => {
        const pageId = e.target.value;
        setSelectedPage(pageId);
        setSelectedForm('');
        setForms([]);

        try {
            setFormsLoading(true);
            const res = await axios.get(`${API_BASE}/pages/${pageId}/forms`, { headers });
            setForms(res.data);
        } catch (error) {
            toast.error("Failed to load forms for this page.");
        } finally {
            setFormsLoading(false);
        }
    };

    const existingMapping = mappings.find(
        (m) => m.pageId === selectedPage && m.formId === selectedForm,
    );

    const handleSave = async () => {
        const page = pages.find(p => p.id === selectedPage);
        const form = forms.find(f => f.id === selectedForm);
        const project = projects.find(p => p.projectId === selectedProject);

        if (!page || !form) return;
        if (!project) {
            toast.error("Please select the project these leads should go to.");
            return;
        }

        try {
            setSaving(true);
            await axios.post(`${API_BASE}/save-selection`, {
                connectionName: connectionName.trim() || `${page.name} · ${form.name}`,
                pageId: page.id,
                pageName: page.name,
                pageAccessToken: page.accessToken,
                formId: form.id,
                formName: form.name,
                projectId: project.projectId,
                projectName: project.projectName,
            }, { headers });
            toast.success(
                existingMapping
                    ? `Updated! ${form.name} now sends leads to ${project.projectName}.`
                    : `Connected! Leads from ${form.name} will go to ${project.projectName}.`,
            );
            setSelectedForm('');
            setSelectedProject('');
            setConnectionName('');
            fetchMappings();
        } catch (error) {
            toast.error(error?.response?.data?.Exception || "Failed to save selection.");
        } finally {
            setSaving(false);
        }
    };

    const saveProjectChange = async (m) => {
        const project = projects.find(p => p.projectId === editProject);
        if (!project) {
            toast.error("Please select a project.");
            return;
        }
        try {
            await axios.put(`${API_BASE}/settings/${m.id}`, {
                projectId: project.projectId,
                projectName: project.projectName,
            }, { headers });
            toast.success(`${m.formName} now sends leads to ${project.projectName}.`);
            setEditingId(null);
            setEditProject('');
            fetchMappings();
        } catch (error) {
            toast.error(error?.response?.data?.Exception || "Failed to update project.");
        }
    };

    const removeMapping = (m) =>
        setConfirm({
            title: 'Remove this connection?',
            msg: `New leads from "${m.formName}" will stop coming into the CRM. Leads already created stay as they are.`,
            label: 'Remove',
            action: async () => {
                try {
                    await axios.delete(`${API_BASE}/settings/${m.id}`, { headers });
                    toast.success("Connection removed.");
                    fetchMappings();
                } catch (error) {
                    toast.error("Failed to remove connection.");
                }
            },
        });

    // Show the project's current name if it was renamed since saving
    const projectLabel = (m) =>
        projects.find(p => p.projectId === m.projectId)?.projectName || m.projectName;

    const missingProject = mappings.filter(m => !m.projectId).length;

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress size={28} thickness={4.5} sx={{ color: PRIMARY }} />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 820, mx: 'auto', mt: 3, fontFamily: FONT, color: INK, textAlign: 'left' }}>
            {/* ================= HEADER / CONNECTION ================= */}
            <Box sx={cardSx}>
                <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 2.5, py: 2 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Box
                            component="span"
                            sx={{
                                flex: '0 0 auto',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                background: '#EAF2FB',
                                border: '1px solid #C9DEF3',
                                color: FB_BLUE,
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.5h-2.8V24C19.62 23.1 24 18.1 24 12.07z" />
                            </svg>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: INK }}>
                                Facebook Lead Ads Sync
                            </Typography>
                            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}>
                                Each lead form sends its leads into the project you choose.
                            </Typography>
                        </Box>
                    </Box>
                    {isConnected && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                                icon={<CheckCircleIcon />}
                                label="Connected"
                                variant="outlined"
                                sx={{
                                    height: 28,
                                    borderRadius: '999px',
                                    background: '#E9F7EE',
                                    border: '1px solid #C4E8D2',
                                    color: '#1B6B41',
                                    fontFamily: FONT,
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    '& .MuiChip-icon': { color: '#2FA864', fontSize: 16 },
                                }}
                            />
                            <Button size="small" onClick={handleDisconnect} sx={dangerBtn}>
                                Remove Connection
                            </Button>
                        </Box>
                    )}
                </Box>

                {!isConnected && (
                    <Box sx={{ textAlign: 'center', py: 4.5, px: 3, borderTop: '1px solid #EDF1F5' }}>
                        <Typography
                            sx={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.55, color: '#4A5A6E', maxWidth: 380, mx: 'auto', mb: 2.5, textWrap: 'pretty' }}
                        >
                            Connect your Facebook account to automatically sync leads directly into your CRM.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleConnect}
                            sx={{ ...primaryBtn, bgcolor: FB_BLUE, '&:hover': { bgcolor: '#166FE5', boxShadow: 'none' } }}
                        >
                            Connect Facebook
                        </Button>
                    </Box>
                )}
            </Box>

            {isConnected && (
                <>
                    {/* ================= ADD A CONNECTION ================= */}
                    <Box sx={cardSx}>
                        <Box sx={cardHeadSx}>
                            <span style={cardTitle}>
                                <span style={dot('#2F7FD1')} />
                                Connect a lead form
                            </span>
                            <span style={{ fontFamily: FONT, fontSize: 12, color: MUTED }}>Page → Form → Project</span>
                        </Box>

                        <Box sx={{ px: 2.5, pt: 2.25, pb: 2.5 }}>
                            {pages.length === 0 ? (
                                <Typography
                                    sx={{
                                        fontFamily: FONT,
                                        fontSize: 13.5,
                                        fontWeight: 500,
                                        color: '#A6342A',
                                        p: 1.5,
                                        background: '#FBEDEB',
                                        border: '1px solid #F2CFCA',
                                        borderRadius: '10px',
                                    }}
                                >
                                    No Facebook Business Pages found on this account.
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: '16px 12px',
                                        gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0,1fr))' },
                                    }}
                                >
                                    <Box>
                                        <label style={lbl}>1. Facebook page<span style={req}>*</span></label>
                                        <ThemedSelect
                                            value={selectedPage}
                                            onChange={handlePageChange}
                                            placeholder="Select Page"
                                            options={pages.map(page => ({ value: page.id, label: page.name }))}
                                            height={44}
                                        />
                                    </Box>

                                    <Box>
                                        <label style={lbl}>2. Lead form<span style={req}>*</span></label>
                                        {formsLoading ? (
                                            <Box sx={{ ...textInp, display: 'flex', alignItems: 'center', gap: 1, color: MUTED, fontSize: 13.5 }}>
                                                <CircularProgress size={14} thickness={5} sx={{ color: MUTED }} /> Loading forms...
                                            </Box>
                                        ) : (
                                            <ThemedSelect
                                                value={selectedForm}
                                                onChange={(e) => setSelectedForm(e.target.value)}
                                                placeholder={selectedPage ? (forms.length ? 'Select Form' : 'No forms on this page') : 'Select a page first'}
                                                options={forms.map(form => {
                                                    const linked = mappings.find(m => m.pageId === selectedPage && m.formId === form.id);
                                                    return { value: form.id, label: linked ? `${form.name}  (connected)` : form.name };
                                                })}
                                                height={44}
                                            />
                                        )}
                                    </Box>

                                    <Box>
                                        <label style={lbl}>3. Send leads to project<span style={req}>*</span></label>
                                        <ThemedSelect
                                            value={selectedProject}
                                            onChange={(e) => setSelectedProject(e.target.value)}
                                            placeholder="Select Project"
                                            options={projects.map(p => ({ value: p.projectId, label: p.projectName }))}
                                            height={44}
                                        />
                                    </Box>

                                    <Box>
                                        <label style={lbl}>Connection name <span style={{ color: MUTED, fontWeight: 500 }}>(optional)</span></label>
                                        <input
                                            type="text"
                                            value={connectionName}
                                            onChange={(e) => setConnectionName(e.target.value)}
                                            placeholder="e.g. Skyline launch campaign"
                                            style={textInp}
                                        />
                                    </Box>

                                    {existingMapping && (
                                        <Box
                                            sx={{
                                                gridColumn: '1 / -1',
                                                p: '10px 12px',
                                                background: '#FDF4E1',
                                                border: '1px solid #F3E0B4',
                                                borderRadius: '10px',
                                                fontFamily: FONT,
                                                fontSize: 13,
                                                color: '#8A5A08',
                                            }}
                                        >
                                            This form is already connected
                                            {existingMapping.projectId ? <> to <b>{projectLabel(existingMapping)}</b></> : ' with no project'}.
                                            Saving will update it instead of adding a new one.
                                        </Box>
                                    )}

                                    <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleSave}
                                            disabled={!selectedPage || !selectedForm || !selectedProject || saving}
                                            sx={{ ...primaryBtn, width: { xs: '100%', sm: 'auto' } }}
                                        >
                                            {saving ? 'Saving...' : existingMapping ? 'Update connection' : 'Add connection'}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* ================= CONNECTED FORMS ================= */}
                    <Box sx={cardSx}>
                        <Box sx={cardHeadSx}>
                            <span style={cardTitle}>
                                <span style={dot('#12A08F')} />
                                Connected forms
                                <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: 0 }}>{mappings.length}</span>
                            </span>
                            {missingProject > 0 && (
                                <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#A6342A' }}>
                                    {missingProject} without a project
                                </span>
                            )}
                        </Box>

                        {mappings.length === 0 ? (
                            <Box sx={{ px: 2.5, py: 4, textAlign: 'center', fontFamily: FONT, fontSize: 13.5, color: MUTED }}>
                                No forms connected yet. Pick a page, form and project above to start syncing leads.
                            </Box>
                        ) : (
                            mappings.map((m, i) => {
                                const noProject = !m.projectId;
                                const editing = editingId === m.id;
                                return (
                                    <Box
                                        key={m.id}
                                        sx={{
                                            px: 2.5,
                                            py: 1.75,
                                            borderTop: i === 0 ? 0 : '1px solid #F1F4F8',
                                            background: noProject ? '#FFF8F7' : '#FFFFFF',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                                            {/* page / form */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {m.connectionName || m.formName}
                                                </Typography>
                                                <Typography sx={{ mt: 0.25, fontFamily: FONT, fontSize: 12.5, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {m.pageName} <span style={{ color: '#B4C0CE' }}>/</span> {m.formName}
                                                </Typography>
                                            </Box>

                                            {/* arrow + project */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: '0 0 auto', minWidth: 0 }}>
                                                <span style={{ color: '#9AA7B8', fontSize: 16 }}>→</span>
                                                {noProject ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', height: 26, padding: '0 10px', borderRadius: 999, background: '#FBEDEB', border: '1px solid #F2CFCA', color: '#A6342A', fontFamily: FONT, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                        No project — leads missed
                                                    </span>
                                                ) : (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', height: 26, padding: '0 10px', borderRadius: 999, background: '#EAF2FB', border: '1px solid #C9DEF3', color: '#1B4F82', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {projectLabel(m)}
                                                    </span>
                                                )}
                                            </Box>

                                            {/* actions */}
                                            {!editing && (
                                                <Box sx={{ display: 'flex', gap: 1, flex: '0 0 auto' }}>
                                                    <Button
                                                        size="small"
                                                        onClick={() => { setEditingId(m.id); setEditProject(m.projectId || ''); }}
                                                        sx={noProject ? { ...ghostBtn, background: PRIMARY, color: '#FFFFFF', border: 0, '&:hover': { background: '#084767' } } : ghostBtn}
                                                    >
                                                        {noProject ? 'Set project' : 'Change project'}
                                                    </Button>
                                                    <Button size="small" onClick={() => removeMapping(m)} sx={dangerBtn}>
                                                        Remove
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* inline project editor */}
                                        {editing && (
                                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                                                    <ThemedSelect
                                                        value={editProject}
                                                        onChange={(e) => setEditProject(e.target.value)}
                                                        placeholder="Select Project"
                                                        options={projects.map(p => ({ value: p.projectId, label: p.projectName }))}
                                                        height={36}
                                                    />
                                                </Box>
                                                <Button size="small" onClick={() => saveProjectChange(m)} disabled={!editProject} sx={{ ...primaryBtn, height: 34, px: 2, fontSize: 13 }}>
                                                    Save
                                                </Button>
                                                <Button size="small" onClick={() => { setEditingId(null); setEditProject(''); }} sx={{ ...ghostBtn, height: 34 }}>
                                                    Cancel
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </>
            )}

            {/* ================= CONFIRM DIALOG (replaces window.confirm) ================= */}
            <Dialog
                open={!!confirm}
                onClose={() => setConfirm(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '18px', p: 2.5, boxShadow: '0 32px 64px -20px rgba(15,27,42,0.5)' } }}
                slotProps={{ backdrop: { sx: { background: 'rgba(15,27,42,0.46)' } } }}
            >
                <Typography sx={{ fontFamily: FONT, fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', color: INK }}>
                    {confirm?.title}
                </Typography>
                <Typography sx={{ mt: 1, fontFamily: FONT, fontSize: 13.5, lineHeight: 1.55, color: '#4A5A6E' }}>
                    {confirm?.msg}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
                    <Button onClick={() => setConfirm(null)} sx={{ ...ghostBtn, height: 38, px: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            const action = confirm?.action;
                            setConfirm(null);
                            if (action) await action();
                        }}
                        sx={{ ...ghostBtn, height: 38, px: 2, background: ERROR, color: '#FFFFFF', border: 0, '&:hover': { background: '#A6342A' } }}
                    >
                        {confirm?.label || 'Confirm'}
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export default FacebookIntegration;